import {CONFIG} from '../appConfig';
import request from '../utils/app/request';
import {batchActions} from '../utils/redux';
import {getFormattedMessages} from '../utils/app/messages';
import {getQsFromObj} from '../utils/js/object';
import { Timeout } from '../utils/app/sessionTimeout';
import {AXIOS_TIMEOUT_ERROR_CODE, REQUEST} from '../constants/index';

/**
 * @private
 * @description Action creator for request starting
 * @param {Object} data {serviceType:String, fetchingKey:String, clearMessagesOnRequest:Boolean, statePath:Array, clearStateOnRequest:Boolean}
 * @returns {Object}
 */
function onRequestStart(data) {
  const action = {
    type: REQUEST.REQUEST_START,
    serviceType:data.serviceType,
    fetchingKey:data.fetchingKey,
    clearMessagesOnRequest:data.clearMessagesOnRequest,
    cancelFetching: data.cancelFetching
  };
  if (Array.isArray(data.statePath)) Object.assign(action, {statePath:data.statePath, clearStateOnRequest:data.clearStateOnRequest});
  return action;
}

/**
 * @private
 * @description Action creator for request error
 * @param {Object} data {serviceType:String, reqUrl:String, payload:Object, fetchingKey:String, messages:Array}
 * @returns {Object}
 */
function onRequestError(data) {
  return {type: REQUEST.REQUEST_ERROR, reqUrl:data.reqUrl, payload:data.payload, serviceType:data.serviceType, fetchingKey:data.fetchingKey, messages:data.messages};
}

/**
 * @private
 * @description Action creator for request success
 * @param {Object} data {serviceType:String, resp:*, statePath:Array}
 * @returns {Object}
 */
function onRequestSuccess(data) {
  if (data.trimRespProp && data.resp[data.trimRespProp]) data.resp = data.resp[data.trimRespProp];
  return {type: REQUEST.REQUEST_SUCCESS, serviceType:data.serviceType, resp:data.resp, statePath:data.statePath};
}

/**
 * @private
 * @description Action creator for request complete
 * @param {Object} data {serviceType:String, fetchingKey:String}
 * @returns {Object}
 */
function onRequestComplete(data) {
  return {type: REQUEST.REQUEST_COMPLETE, serviceType:data.serviceType, fetchingKey:data.fetchingKey};
}

/**
 * @private
 * @description Action creator for updating the GMA or CDA session in redux store
 * @param {Object} session
 * @param {String} serviceType
 * @returns {Object}
 */

function updateSession(session, serviceType) {
  return {type: REQUEST.UPDATE_SESSION, session, serviceType};
}

/**
 * @private
 * @description Action creator to notify when session timed out
 * @returns {Object}
 */
const onSessionTimedOut = () => {
  return {type: REQUEST.SESSION_TIMED_OUT};
};

/**
 * This is the format used by GMI service configurations for the `request` action (see {@link actions.request}).
 * @typedef {object} CommitPostData
 * @prop {object} payload data to POST to the server, for the request service
 * @prop {object} params the query string parameters for the request service
 * @see actions.reservations  {@link actions.reservations.commit}
 * @memberof actions
 */

/**
 * @name request
 * @description Gma/Solr request action wrapper
 * @param {Object} config `{data, opts, service}`, where `data` is {@link actions.CommitPostData}
 * @returns {Function}
 * @memberof actions
 * @example
 * let cancel = null;
 * const data = {...}; // *(Object)*: data passed to service; data is in format {params:object, payload:object}
 * const opts = {
 *  cancelFn: c => cancel = c, // *(Function)*: Optional. 'cancel' should be a value stored outside the opts
 *  clearMessagesOnRequest: true, // *(Boolean)*: defaults to `true`
 *  clearStateOnRequest: false, // *(Boolean)*: defaults to `false`
 *  headers: {}, // *(Object)*: Optional. Object of custom headers for the request
 *  updateSessionOnResp: true, // *(Boolean)*: defaults to `true`
 *  fullResponse: false, // *(Boolean)*: defaults to `false`
 *  addFetchingKeyInResponse: false // *(Boolean)*: Adds the "fetchingKey" in the response of the request. Defaults to `false`.
 * };
 * const service = services.gma.reservations.carClassCode; // *(Function)*: service function for request
 * // if you want to cancel an existing request
 * if (cancel !== null) {
 *  cancel();
 *  cancel = null;
 * }
 * dispatch(actions.request({data, opts, service}))
 *  // checking resp.cancelled is only necessary if you use cancelFn option
 *  // if a request was cancelled; resp.cancelled === true
 *  .then(resp => {
 *    if (!resp.cancelled) resp.messages ? error() : success();
 *    cancel = null;
 *  });
 *
 * // services noted as fetching via...
 * // state.gmi.fetching.gma (boolean)
 * // state.gmi.fetching.gma_services (array of active requests)
 * // state.gmi.fetching.solr (boolean)
 * // state.gmi.fetching.solr_services (array of active requests)
 */
export default function (config) {
  return dispatch => {
    const serviceConfig = config.service(config.data);
    let serviceType;
    if (serviceConfig.origin === CONFIG.GMA_ORIGIN_URL) {
      serviceType = REQUEST.GMA;
    } else if (serviceConfig.origin === CONFIG.CDA_ORIGIN_URL) {
      serviceType = REQUEST.CDA;
    } else if (serviceConfig.origin === CONFIG.SOLR_ORIGIN_URL) {
      serviceType = REQUEST.SOLR;
    }

    const {opts = {}} = config;
    const {
      clearMessagesOnRequest = true,
      clearStateOnRequest = false,
      updateSessionOnResp = true,
      addFetchingKeyInResponse = false,
      cancelFetching,
      allowWarnPriorityErrors = false,
      timeoutConfig
    } = opts;
    const fetchingKey = serviceConfig.endpoint + (serviceConfig.params ? getQsFromObj(serviceConfig.params) : '');

    dispatch(onRequestStart({
      serviceType,
      fetchingKey,
      clearMessagesOnRequest,
      statePath: serviceConfig.statePath,
      clearStateOnRequest,
      cancelFetching
    }));
    if (opts.cancelFn) Object.assign(serviceConfig, {cancelFn:opts.cancelFn});
    if (opts.headers) Object.assign(serviceConfig, {headers:opts.headers});
    if (opts.fullResponse) Object.assign(serviceConfig, {fullResponse:opts.fullResponse});
    if (opts.timeoutConfig) Object.assign(serviceConfig, {timeoutConfig:opts.timeoutConfig});

    return request(serviceConfig)
      .then(
        resp => {
          const response = resp; // save potential reference to fullResponse
          resp = (opts.fullResponse) ? resp.data : resp;
          const actions = [];
          let hasWarnPriority = false;

          if (serviceType === REQUEST.GMA || serviceType === REQUEST.CDA) {
            // session should update independent of error messages
            const session = resp.session ? resp.session : (resp.gbo || resp.deem ? resp : null);
            if (session !== null && updateSessionOnResp) actions.push(updateSession(session, serviceType));
            // filter out any silent and determine if there is a blocking error
            if (resp.messages && resp.messages.length > 0) {
              resp.messages = resp.messages.reduce((next, message) => {
                const {display_as, priority} = message;
                if (display_as !== 'silent') {
                  if ( (display_as === 'STATUS_I18N_CODE' && CONFIG.STATUS_I18N_CODE) || message.message === "") {
                    message.message = CONFIG.ERROR_STATUS[message.code] || CONFIG.ERROR_DEFAULT;
                  }

                  if(priority === 'WARN'){
                    hasWarnPriority = true;
                  }

                  next.push(message);
                }
                return next;
              }, []);
            }
          }
          if (resp.messages && resp.messages.length > 0) {
            const reqUrl = serviceConfig.origin + fetchingKey;

            if(hasWarnPriority && allowWarnPriorityErrors){
              actions.push(onRequestSuccess({serviceType, resp, statePath: serviceConfig.statePath, trimRespProp: serviceConfig.trimRespProp}));
            } else {
              actions.push(onRequestError({serviceType, reqUrl, payload:serviceConfig.data, fetchingKey, messages:resp.messages}));
            }
          } else if (Array.isArray(serviceConfig.statePath)) {
            actions.push(onRequestSuccess({serviceType, resp, statePath: serviceConfig.statePath, trimRespProp: serviceConfig.trimRespProp}));
          }
          if (actions.length > 1) {
            dispatch(batchActions(...actions));
          } else if (actions.length === 1) {
            dispatch(actions[0]);
          }
          return response;
        },
        err => {
          const messages = getFormattedMessages(err.message, err.exception, err.axiosErrorCode);
          const { cancelled, axiosErrorCode } = err;

          // if axios throws a timeout error and we provided a callback, then we call this callback
          if(axiosErrorCode === AXIOS_TIMEOUT_ERROR_CODE && timeoutConfig?.onRequestTimeoutCallback) {
            return timeoutConfig?.onRequestTimeoutCallback();
          }

          if (!cancelled) dispatch(onRequestError({ serviceType, fetchingKey, messages }));
          return { cancelled, messages, axiosErrorCode };
        }
      )
      .then(resp => {
        dispatch(onRequestComplete({ serviceType, fetchingKey }));

        if (resp?.session_timeout) {
          Timeout.init(resp.session_timeout, () => dispatch(onSessionTimedOut()));
        }

        if (addFetchingKeyInResponse) {
          Object.assign(resp, { fetchingKey });
        }

        return resp;
      });
  };
}
